import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import Axios from 'axios';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet-async';

import {
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
} from 'react-share';

import { NewLoginForm } from './NewLoginDailogForm';
import { ContactForm } from './ContactFormDailog';
import { BackDropLoad } from './LoadingBackDrop';
import { Loading } from './Loading';
import { UserContext } from './userContext';
import { PageContainer, Protected } from '../theme/primitives';
import { ShareGlyph, ShareBubble } from './ShareIcons';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ─────────── Inline icons ─────────── */

const Icon = {
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  ExternalLink: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
    </svg>
  ),
  Link: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
};

/* ─────────── Definition row ─────────── */

function DefRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[120px_1fr] sm:grid-cols-[160px_1fr] gap-3 py-2.5 border-b border-border-subtle/70 last:border-b-0">
      <dt className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary self-start">{label}</dt>
      <dd className="text-[13px] font-medium text-fg-primary leading-snug">{value}</dd>
    </div>
  );
}

/* ═══════════════════ SingleCareer ═══════════════════ */

export const SingleCareer = () => {
  const { id } = useParams();
  const url = window.location.href;
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [wait, setWait] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(!user);
  const [showContactForm, setShowContactForm] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/get-single-career', { id }, { headers: { key } }).then((res) => {
      setData(res.data?.data?.[0] || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <Loading />;

  return (
    <Protected>
      <BackDropLoad wait={wait} />
      <NewLoginForm
        strict
        strictTitle="Sign in to view careers"
        strictMessage="Career listings are available to ESAY members. Log in to view full details."
        open={showLoginForm}
        showload={() => setWait(true)}
        hideload={() => setWait(false)}
        closeform={() => setShowLoginForm(false)}
        openloginform={() => setShowLoginForm(true)}
      />

      {data && (
        <Helmet>
          <title>{data.job_title} - Careers | ESAY</title>
        </Helmet>
      )}

      <PageContainer maxWidth="md">
        <div
          className="transition-[filter] duration-300 ease-out-soft"
          style={user ? undefined : { filter: 'blur(4px)', pointerEvents: 'none' }}
        >
          {data && (
            <article className="pt-4 pb-8">

              {/* ═════ Compact header ═════ */}
              <header>
                {/* Posted-by + location inline */}
                <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-bold uppercase tracking-[0.16em]">
                  <span className="text-amber">{data.posted_by}</span>
                  {data.job_location && (<>
                    <span aria-hidden className="text-fg-tertiary">·</span>
                    <span className="text-fg-tertiary normal-case tracking-normal text-[12px] font-medium">{data.job_location}</span>
                  </>)}
                </div>

                {/* Title - moderate size */}
                <h1 className="text-[22px] sm:text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-fg-primary">
                  {data.job_title}
                </h1>

                {/* Amber accent */}
                <div aria-hidden className="mt-3 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0" />

                {/* Inline meta */}
                <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-fg-secondary">
                  {data.job_type && <span>{data.job_type}</span>}
                  {data.current_openings && (<>
                    <span aria-hidden className="text-fg-tertiary">·</span>
                    <span>{data.current_openings} {parseInt(data.current_openings) === 1 ? 'opening' : 'openings'}</span>
                  </>)}
                  {data.posted_date && (<>
                    <span aria-hidden className="text-fg-tertiary">·</span>
                    <span>Posted {data.posted_date}</span>
                  </>)}
                  {data.last_date && (<>
                    <span aria-hidden className="text-fg-tertiary">·</span>
                    <span className="text-amber-light font-semibold">Apply by {data.last_date}</span>
                  </>)}
                </div>

                {/* CTA + Share row */}
                <div className="mt-5 flex flex-wrap items-center gap-2.5">
                  {data.type === '1' ? (
                    <button
                      type="button"
                      onClick={() => window.open(data.job_url, '_blank', 'noopener,noreferrer')}
                      className="
                        group inline-flex h-10 items-center justify-center gap-1.5
                        rounded-full px-5
                        text-[12px] font-bold uppercase tracking-[0.1em]
                        bg-amber text-ink
                        hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.25),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                        active:scale-[0.98]
                        transition-all duration-200 ease-out-soft
                      "
                    >
                      View details
                      <Icon.ExternalLink className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        if (user) setShowContactForm(true);
                        else setShowLoginForm(true);
                      }}
                      className="
                        group inline-flex h-10 items-center justify-center gap-1.5
                        rounded-full px-5
                        text-[12px] font-bold uppercase tracking-[0.1em]
                        bg-amber text-ink
                        hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.25),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                        active:scale-[0.98]
                        transition-all duration-200 ease-out-soft
                      "
                    >
                      Apply now
                      <Icon.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                  )}

                  {/* Compact share dock */}
                  <div className="flex items-center gap-1 ml-auto">
                    <FacebookShareButton url={url}><ShareBubble label="Facebook" size="sm"><ShareGlyph.Facebook /></ShareBubble></FacebookShareButton>
                    <LinkedinShareButton url={url}><ShareBubble label="LinkedIn" size="sm"><ShareGlyph.LinkedIn /></ShareBubble></LinkedinShareButton>
                    <WhatsappShareButton url={url}><ShareBubble label="WhatsApp" size="sm"><ShareGlyph.WhatsApp /></ShareBubble></WhatsappShareButton>
                    <button
                      type="button"
                      onClick={handleCopy}
                      aria-label={copied ? 'Link copied' : 'Copy link'}
                      title={copied ? 'Link copied!' : 'Click to copy the URL'}
                      className={`
                        inline-flex h-9 w-9 items-center justify-center rounded-full
                        border border-border-subtle bg-surface-elevated
                        transition-[background-color,border-color,color,transform] duration-200 ease-out-soft
                        hover:bg-amber hover:border-amber hover:text-ink hover:-translate-y-0.5
                        ${copied ? 'text-amber' : 'text-fg-secondary'}
                      `}
                    >
                      {copied ? <Icon.Check className="h-[15px] w-[15px]" /> : <Icon.Link className="h-[15px] w-[15px]" />}
                    </button>
                  </div>
                </div>
              </header>

              {/* ═════ Key details - compact definition list ═════ */}
              <section className="mt-7">
                <div className="mb-3 flex items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Key details</span>
                  <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-amber/50 to-transparent" />
                </div>
                <dl className="rounded-lg border border-border-subtle bg-surface-paper px-4">
                  <DefRow label="Posted by" value={data.posted_by} />
                  <DefRow label="Posted date" value={data.posted_date} />
                  <DefRow label="Type" value={data.job_type} />
                  <DefRow label="Openings" value={data.current_openings} />
                  <DefRow label="Qualification" value={data.job_qualification} />
                  <DefRow label="Start date" value={data.start_date} />
                  <DefRow label="Last date to apply" value={data.last_date} />
                  <DefRow label="Address" value={data.job_address} />
                </dl>
              </section>

              {/* ═════ Job description - white card ═════ */}
              {data.job_desc && (
                <section className="mt-6">
                  <div className="mb-3 flex items-center gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Description</span>
                    <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-amber/50 to-transparent" />
                  </div>
                  <div
                    className="rounded-lg border p-4 sm:p-5"
                    style={{
                      backgroundColor: '#FFFFFF',
                      borderColor: 'rgba(10,10,11,0.08)',
                      boxShadow: '0 4px 16px -8px rgba(0,0,0,0.4)',
                    }}
                  >
                    <div
                      className="
                        text-[14px] leading-[1.65]
                        [&_p]:mb-2.5
                        [&_a]:text-amber-deep [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-amber
                        [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md [&_img]:my-3
                        [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[18px] [&_h1]:font-bold [&_h1]:text-ink
                        [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-[16px] [&_h2]:font-bold [&_h2]:text-ink
                        [&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-[15px] [&_h3]:font-bold [&_h3]:text-ink
                        [&_h4]:mt-3 [&_h4]:mb-1.5 [&_h4]:text-[14px] [&_h4]:font-bold [&_h4]:text-ink
                        [&_strong]:text-ink [&_strong]:font-bold
                        [&_b]:text-ink [&_b]:font-bold
                        [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:my-1.5
                        [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:my-1.5
                        [&_li]:my-0.5
                        [&_blockquote]:border-l-[3px] [&_blockquote]:border-amber [&_blockquote]:pl-3 [&_blockquote]:my-2.5 [&_blockquote]:italic [&_blockquote]:text-[#444]
                        [&_table]:w-full [&_table]:my-2.5 [&_table]:border-collapse [&_table]:bg-[#FAFAFA] [&_table]:rounded-md [&_table]:overflow-hidden
                        [&_th]:bg-[#F0F0EE] [&_th]:text-ink [&_th]:font-bold [&_th]:p-2 [&_th]:text-left [&_th]:border [&_th]:border-black/10
                        [&_td]:p-2 [&_td]:text-left [&_td]:border [&_td]:border-black/10
                      "
                      style={{ color: '#1f1f23' }}
                    >
                      {parse(data.job_desc)}
                    </div>
                  </div>
                </section>
              )}
            </article>
          )}
        </div>

        {user && (
          <ContactForm
            open={showContactForm}
            showload={() => setWait(true)}
            hideload={() => setWait(false)}
            closeform={() => setShowContactForm(false)}
            data={data}
            url="careers"
          />
        )}
      </PageContainer>
    </Protected>
  );
};
